import z, { ZodObject, ZodRawShape } from 'zod'

export const makeErpPaginatedSchema = <T extends ZodRawShape>(
  dataSchema: ZodObject<T>
) =>
  z.object({
    data: z.array(dataSchema),
    meta: z.object({
      total: z.number(),
      limit: z.number(),
      offset: z.number(),
    }),
  })

export const makeErpListSchema = <T extends ZodRawShape>(
  dataSchema: ZodObject<T>
) =>
  z.object({
    data: z.array(dataSchema),
  })

// For pagination cursor is our page value
export type PaginationOptions = {
  limit: number
  /**
   * Cursor / page
   */
  cursor: number
}

/**
 * Since trpc forces us to use "cursor" instead of "offset"/"page" we need both schemas
 */
export const ErpPaginationOptionsSchema = z.object({
  limit: z.number().min(0).default(20),
  offset: z.number().min(0).default(0),
})

export type ErpPaginationOptions = z.infer<typeof ErpPaginationOptionsSchema>

export const PaginationOptionsSchema = z.object({
  limit: z.number().min(0).default(20),
  cursor: z.number().default(1),
})

export const getPaginationOptions = (
  pagination?: Partial<PaginationOptions>
) => {
  const { limit = 20, cursor = 1 } = pagination || {}
  if (cursor < 1) {
    throw new Error('cursor starts at 1')
  }
  return {
    limit: pagination?.limit || 10,
    offset: limit * Math.max(cursor - 1, 0),
  }
}

export type ErpPaginatedResponse<T> = {
  data: T[]
  meta: {
    total: number
    limit: number
    offset: number
  }
}

export type ErpResponse<T> = {
  data: T
}

export type ErpCodeResponse<T> = {
  data: T
  code: number
  error?: string
}

export type PaginatedResponse<T> = {
  data: T[]
  meta: {
    total: number
    limit: number
    cursor: number
    nextCursor: number | null
  }
}

export const fromErpPaginatedResponse = <T>(req: ErpPaginatedResponse<T>) => {
  const cursor = req.meta.offset / req.meta.limit + 1
  const end = req.meta.offset + req.meta.limit

  return {
    data: req.data,
    meta: {
      total: req.meta.total,
      limit: req.meta.limit,
      cursor: cursor,
      nextCursor: end < req.meta.total ? cursor + 1 : null,
    },
  } satisfies PaginatedResponse<T>
}
